"use client";

import Image from "next/image";
import Link from "next/link";
import {
  getImageUrl,
  isValidImageUrl,
  getPlaceholderImage,
} from "@/utils/image";
import { truncateText } from "@/utils/text";
import { NewsItem } from "@/types/newsType";

interface NewsCardProps {
  news: NewsItem;
  size?: "small" | "medium" | "large";
}

export function NewsCard({ news, size = "medium" }: NewsCardProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const cardSizes = {
    small: "max-w-xs",
    medium: "max-w-sm",
    large: "max-w-md",
  };

  const imageSizes = {
    small: "h-40",
    medium: "h-48",
    large: "h-56",
  };

  // Get the processed image URL
  const processedImageUrl = getImageUrl(news.img?.url);

  // Determine the final image source
  const imageSrc = isValidImageUrl(processedImageUrl)
    ? processedImageUrl
    : getPlaceholderImage(400, 225, "News");

  return (
    <article
      className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden ${cardSizes[size]}`}
    >
      <div className={`relative ${imageSizes[size]} w-full`}>
        <Image
          src={imageSrc}
          alt={news.title || "News image"}
          fill
          className="object-cover"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />
      </div>

      <div className="p-4">
        <div className="flex items-center text-sm text-gray-500 mb-2">
          <span>Ngày đăng: {formatDate(news.createdAt)}</span>
        </div>

        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 leading-snug">
          {news.title}
        </h3>

        <div className="text-gray-600 text-sm leading-relaxed mb-3">
          <div
            className="line-clamp-2"
            dangerouslySetInnerHTML={{
              __html: truncateText(
                typeof news.content === "string"
                  ? JSON.parse(news.content).html
                  : news.content.html,
                150
              ),
            }}
          />
        </div>

        <Link
          href={`/news/${news.slug || news.id}`}
          className="inline-block text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
        >
          Đọc thêm →
        </Link>
      </div>
    </article>
  );
}
